import React, { useEffect, useState } from "react";
import {
  Task,
  TaskType,
  CheckboxTask,
  ResultPayload,
  VideoMultiTask,
  ImageSortingTask,
  ImagePlacementTask,
  LanguageAnalysisTask,
  AudioRecordings,
  CheckboxAnswer,
} from "data-models";
import { TaskResult } from "data-models/taskresult";
import Tasks from "../locales/Tasks.json";
import { apiClient } from "./apiClient";

const TaskService = {
  taskIsAnswered: (task: Task) => {
    const hasAnyChecked = (ans: CheckboxAnswer[] | Record<string, CheckboxAnswer[]>): boolean => {
      if (Array.isArray(ans)) return ans.some(a => a.checked);
      if (ans && typeof ans === "object")
        return Object.values(ans).some(arr => Array.isArray(arr) && arr.some(a => a.checked));
      return false;
    };
    if (task.isSkipped) return true;
    switch (task.type) {
      case TaskType.Video:
      case TaskType.Audio:
      case TaskType.Checkbox:
      case TaskType.ImageSelection:
      case TaskType.ImageSelectSingleStep:
      case TaskType.Familjen_3:
      case TaskType.Familjen_5:
      case TaskType.ImageClick:
        return (task as CheckboxTask).answerOptions.some(a => a.checked);
      case TaskType.ImageSelectMultipleSteps: {
        const options = Array.isArray(task.answerOptions) ? task.answerOptions : [];
        const imagesPerStep = Math.max(1, Number((task as any).imagesPerStep ?? 1));
        const configuredSteps = Number((task as any).numberOfSteps ?? 0);
        const computedSteps = Math.max(1, Math.ceil(options.length / imagesPerStep));
        const totalSteps = configuredSteps > 0 ? configuredSteps : computedSteps;

        for (let stepIndex = 0; stepIndex < totalSteps; stepIndex += 1) {
          const start = stepIndex * imagesPerStep;
          const end = start + imagesPerStep;
          const stepOptions = options.slice(start, end);
          if (task.image_selection_disabled) {
            if (!stepOptions.some((option: any) => option.isAnswered)) {
              return false;
            }
          } else {
            if (!stepOptions.some((option: any) => option.checked)) {
              return false;
            }
          }
        }
        return true;
      }
      case TaskType.Familjen_29: {
        const ao = (task as CheckboxTask).answerOptions as CheckboxAnswer[] | Record<string, CheckboxAnswer[]>;
        return hasAnyChecked(ao);
      }
      case TaskType.ImagePlacement:
        return (task as ImagePlacementTask).answerOptions.every(a => !!a.placedAtPoint);
      case TaskType.ImageSorting:
        return (task as ImageSortingTask).answerOptions.every(a => !!a.placedAtPoint);
      case TaskType.VideoMulti:
        return (
          (task as VideoMultiTask).answerOptions.a.some(a => a.checked) &&
          (task as VideoMultiTask).answerOptions.b.some(a => a.checked)
        );
      case TaskType.LanguageAnalysis:
        return (task as LanguageAnalysisTask).audioRecorded;
      case TaskType.RadioButtonMultipleSteps: {
        const options = Array.isArray(task.answerOptions) ? task.answerOptions : [];
        const imagesPerStep = Math.max(1, Number((task as any).imagesPerStep ?? 1));
        const configuredSteps = Number((task as any).numberOfSteps ?? 0);
        const computedSteps = Math.max(1, Math.ceil(options.length / imagesPerStep));
        const totalSteps = configuredSteps > 0 ? configuredSteps : computedSteps;

        for (let stepIndex = 0; stepIndex < totalSteps; stepIndex += 1) {
          const start = stepIndex * imagesPerStep;
          const end = start + imagesPerStep;
          const stepOptions = options.slice(start, end);

          if (!stepOptions.some((option: any) => option.isAnswered)) {
            return false;
          }
        }
        return true;
      }
      default:
        return false;
    }
  },

  answerSummary: (tasks: Task[]): TaskResult[] => {
    const tasksAnswers = tasks.map((task: Task) => {
      let answersFromArray;
      if (task.type !== "language_analysis") {
        answersFromArray = answerArr => {
          return answerArr.map(answerOption => {
            switch (answerOption.type) {
              case TaskType.ImagePlacement:
                return {
                  optionType: answerOption.type,
                  correctPoint: answerOption.correctPoint,
                  placedAtPoint: answerOption.placedAtPoint,
                };
              case TaskType.LanguageAnalysis:
                return {
                  audioUrl: answerOption.audioUrl,
                  optionId: answerOption.id,
                  optionType: answerOption.type,
                };

              default:
                return {
                  optionId: answerOption.id,
                  optionType: answerOption.type,
                  checked: answerOption.checked,
                };
            }
          });
        };
      }
      let collectedAnswers;
      if (task.type !== "language_analysis") {
        switch (task.type) {
          case TaskType.VideoMulti:
            collectedAnswers = {
              a: answersFromArray(task.answerOptions.a),
              b: answersFromArray(task.answerOptions.b),
            };
            break;
          case TaskType.Familjen_29:
            collectedAnswers = {
              a: answersFromArray(task.answerOptions.a),
              b: answersFromArray(task.answerOptions.b),
              c: answersFromArray(task.answerOptions.c),
              d: answersFromArray(task.answerOptions.d),
              e: answersFromArray(task.answerOptions.e),
              f: answersFromArray(task.answerOptions.f),
            };
            break;
          default:
            collectedAnswers = answersFromArray(task.answerOptions);
            break;
        }
      }
      return {
        taskId: task.id,
        answers: collectedAnswers,
        isSkipped: task.isSkipped,
        skipReason: task.skipReason,
      };
    });
    return tasksAnswers;
  },

  audioSubmit: (tasks: Task[]) => {
    const audioArray: AudioRecordings[] = [];

    tasks.forEach(task => {
      if (task.type === "language_analysis" && task.audioRecordings) {
        task.audioRecordings.forEach(audioRecording => {
          const audioObject = {
            audioLength: audioRecording.audioLength,
            audioFile: audioRecording.audioFile,
            taskId: task.id,
            isSkipped: task.isSkipped,
            skipReason: task.skipReason,
          };

          audioArray.push(audioObject);
        });
      }
    });
    return audioArray;
  },

  canSubmitTasks: (qs: Task[]) => qs.every(q => TaskService.taskIsAnswered(q)),

  submitFollowUp: (result: ResultPayload) => {
    const formData = new FormData();

    // Append all properties except audioRecordings to formData
    Object.keys(result).forEach(key => {
      if (key !== "audioRecordings") {
        formData.append(key, result[key]);
      }

      if (key === "audioRecordings" && Array.isArray(result[key])) {
        const audioArray = result[key];
        audioArray.forEach((audio, index) => {
          // Append audioLength
          formData.append(`audioRecordings[${index}].audioLength`, audio.audioLength.toString());

          // Append taskId
          formData.append(`audioRecordings[${index}].taskId`, audio.taskId);

          // Append audioFile from FormData object
          if (audio.audioFile instanceof Blob) {
            formData.append(`audioRecordings[${index}].audioFile`, audio.audioFile, `${index}.wav`);
          }

          //Append isSkipped from FormData object
          formData.append(`audioRecordings[${index}].isSkipped`, audio.isSkipped.toString());

          //Append skipReason from FormData object
          formData.append(
            `audioRecordings[${index}].skipReason`,
            audio?.skipReason?.toString() ?? "", // Provide a default empty string if skipReason is undefined
          );

          //Append audioLanguage
          const browserLocaleLanguage = navigator.language.split(/[-_]/)[0];
          let siteLanguage;
          if (browserLocaleLanguage === "sv") {
            siteLanguage = "se";
          } else if (browserLocaleLanguage === "de") {
            siteLanguage = "de";
          } else {
            siteLanguage = "en";
          }

          let audioLanguageSelected;

          if (sessionStorage.getItem("language-selected")) {
            audioLanguageSelected = sessionStorage.getItem("language-selected");
          } else {
            audioLanguageSelected = siteLanguage;
          }

          let audioLanguageFormat;
          if (audioLanguageSelected === "se") {
            audioLanguageFormat = "sv-SE";
          } else if (audioLanguageSelected === "en") {
            audioLanguageFormat = "en-GB";
          } else if (audioLanguageSelected === "de") {
            audioLanguageFormat = "de-DE";
          }
          formData.append(`audioRecordings[${index}].audioLanguage`, audioLanguageFormat.toString());
        });
      }
    });

    return apiClient
      .callApi({
        method: "POST",
        url: `/followups/${result.followUpId}/result`,
        data: formData,
      })
      .then(response => {
        console.log("Follow-up submitted successfully:", response);
        return response;
      })
      .catch(error => {
        console.error("Error submitting follow-up:", error);
        throw error;
      });
  },

  getAnsweredCount: (qs: Task[]) => qs.filter(TaskService.taskIsAnswered).length,
};

export { TaskService };

export const useTaskService = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksEn, setTasksEn] = useState<Task[]>(Tasks as []);
  // const getTasks = () =>
  //   axiosAPIInstance.get('/tasks').then(({ data }) => data as Task[])

  // const refreshTasks = async () => {
  //   const response = await getTasks()
  //   if (response && response.length > 0) {
  //     setTasks(tasksEn)
  //   }
  // }
  // useEffect(() => {
  //   refreshTasks()
  // }, [])
  useEffect(() => {
    setTasks(tasksEn);
  });

  return { tasks };
};
